import type {
  AudioOverviewErrorCode,
  AudioOverviewErrorDetails,
} from '@/services/audioOverviews';
import type {
  AudioOverviewFormat,
  AudioOverviewLength,
  AudioOverviewSummary,
} from '@/types/AudioOverview';

/** Product copy for Audio Overviews (ORA-735), in one place. */

export interface AudioOverviewFormatInfo {
  format: AudioOverviewFormat;
  name: string;
  description: string;
  /** Approximate minutes per length; BRIEF has one fixed range. */
  minutes: Record<AudioOverviewLength, string>;
}

export const AUDIO_OVERVIEW_FORMAT_INFO: AudioOverviewFormatInfo[] = [
  {
    format: 'DEEP_DIVE',
    name: 'Deep Dive',
    description: 'Mechanisms, connections and analogies that make it click.',
    minutes: { SHORT: '~6 min', DEFAULT: '~12 min', LONG: '~20 min' },
  },
  {
    format: 'BRIEF',
    name: 'Brief',
    description: 'The core ideas in a quick recap.',
    minutes: { SHORT: '~2–3 min', DEFAULT: '~2–3 min', LONG: '~2–3 min' },
  },
  {
    format: 'BOARD_REVIEW',
    name: 'Board Review',
    description:
      'High-yield facts, buzzwords, mnemonics and classic vignettes.',
    minutes: { SHORT: '~5 min', DEFAULT: '~10 min', LONG: '~15 min' },
  },
  {
    format: 'DEBATE',
    name: 'Debate',
    description: 'The hosts argue differentials and approaches.',
    minutes: { SHORT: '~5 min', DEFAULT: '~10 min', LONG: '~15 min' },
  },
];

export const audioOverviewFormatInfo = (
  format: AudioOverviewFormat
): AudioOverviewFormatInfo =>
  AUDIO_OVERVIEW_FORMAT_INFO.find((info) => info.format === format) ??
  AUDIO_OVERVIEW_FORMAT_INFO[0];

export const AUDIO_OVERVIEW_LENGTH_LABELS: Record<AudioOverviewLength, string> =
  {
    SHORT: 'Short',
    DEFAULT: 'Default',
    LONG: 'Long',
  };

export const AUDIO_OVERVIEW_FOCUS_PLACEHOLDER =
  'e.g. Focus on the renal pathology and skip the embryology';

/** "Deep Dive · Long", or just "Brief" (Brief has no length choice). */
export const describeAudioOverviewFormat = (
  overview: Pick<AudioOverviewSummary, 'format' | 'length'>
): string => {
  const { name } = audioOverviewFormatInfo(overview.format);
  if (overview.format === 'BRIEF') return name;
  return `${name} · ${AUDIO_OVERVIEW_LENGTH_LABELS[overview.length]}`;
};

/** The student-facing stage of a generating overview. */
export const audioOverviewStageLabel = (
  status: AudioOverviewSummary['status']
): string => {
  switch (status) {
    case 'QUEUED':
      return 'Queued';
    case 'SCRIPTING':
      return 'Writing script';
    case 'SYNTHESIZING':
      return 'Recording';
    case 'STITCHING':
      return 'Finishing';
    case 'READY':
      return 'Ready';
    case 'FAILED':
      return 'Failed';
  }
};

/** Why a finished-but-failed overview failed, in the student's words. */
export const audioOverviewFailureCopy = (
  errorCode: string | null,
  lectureCount = 2
): string => {
  switch (errorCode) {
    case 'NO_SOURCE_CONTENT':
      return lectureCount === 1
        ? 'There wasn’t enough content in this lecture to talk about.'
        : 'There wasn’t enough content in these lectures to talk about.';
    case 'TIMED_OUT':
      return 'This took too long to generate.';
    default:
      return 'Something went wrong while generating.';
  }
};

const formatResetTime = (resetsAt: string | undefined): string | null => {
  if (!resetsAt) return null;
  const date = new Date(resetsAt);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

/** Friendly copy for every error the create/retry endpoints can return. */
export const audioOverviewErrorCopy = (
  code: AudioOverviewErrorCode,
  details: AudioOverviewErrorDetails = {},
  /** How many lectures were selected; picks singular or plural copy. */
  lectureCount = 2
): string => {
  switch (code) {
    case 'AUDIO_OVERVIEW_DAILY_LIMIT': {
      const resetTime = formatResetTime(details.resetsAt);
      const when = resetTime ? `after ${resetTime}` : 'tomorrow';
      // RUNS counts generation runs (retries included), not new overviews.
      if (details.reason === 'RUNS') {
        return `You’ve reached today’s generation limit. You can generate again ${when}.`;
      }
      const limit = details.limit ?? 5;
      return `You’ve made ${limit} audio overviews today, the daily limit. You can make more ${when}.`;
    }
    case 'AUDIO_OVERVIEW_NOT_RETRYABLE':
      // One episode per lecture set (ORA-810): it has to go before a new one.
      return 'This one can’t be retried. Delete it, then create a new audio overview.';
    case 'AUDIO_OVERVIEW_ALREADY_EXISTS':
      return 'You already have an audio overview for these lectures.';
    case 'AUDIO_OVERVIEW_INVALID_REQUEST':
      return 'Something about these options wasn’t accepted. Check them and try again.';
    case 'AUDIO_OVERVIEW_IN_PROGRESS_LIMIT': {
      const max = details.maxInProgress ?? 2;
      return `You already have ${max} audio overviews generating. Try again when one finishes.`;
    }
    case 'AUDIO_OVERVIEW_INVALID_LECTURES':
      return 'Some of the selected items can’t be used. Select individual lectures (not folders) and try again.';
    case 'AUDIO_OVERVIEW_NO_SOURCE_CONTENT':
      return lectureCount === 1
        ? 'This lecture doesn’t have enough content for an audio overview yet. Add more lectures, or try again once mapping finishes.'
        : 'These lectures don’t have enough content for an audio overview yet. Add more lectures, or try again once mapping finishes.';
    case 'AUDIO_OVERVIEW_UNAVAILABLE':
      return 'Audio overviews are temporarily unavailable. Please try again later.';
    case 'AUDIO_OVERVIEW_UPGRADE_REQUIRED':
    case 'LOCKED':
      return 'Audio overviews are included with paid plans.';
    case 'AUDIO_OVERVIEW_NOT_READY':
      return 'This audio overview isn’t ready yet.';
    case 'ARTICLE_AUDIO_DAILY_LIMIT':
      return 'Today’s audio for library articles has been used up. Try again tomorrow.';
    case 'ARTICLE_AUDIO_IN_PROGRESS_LIMIT':
      return 'Too much article audio is generating right now. Try again in a few minutes.';
    case 'NOT_FOUND':
      return 'Audio overviews aren’t available right now.';
    case 'UNKNOWN':
      return 'Something went wrong. Please try again.';
  }
};

/** "12 min", "45 sec". */
export const formatAudioOverviewDuration = (seconds: number | null): string => {
  if (seconds === null || !Number.isFinite(seconds) || seconds <= 0) return '';
  if (seconds < 60) return `${Math.round(seconds)} sec`;
  return `${Math.round(seconds / 60)} min`;
};
